import { create } from 'zustand';
import axiosInstance from '../../../configs/axios';
import { ApiPath } from '../../../constants/apiPath';
import { axiosErrorMessage } from '../../../utils/error-message';
import { AxiosError } from 'axios';
import CusttomAlertController from '../../../alert/controller/custom.alert.controller';
import { CustomAlertType } from '../../../alert/data/custom.alert.type';
import { CustomAlertIcons } from '../../../alert/data/custom.alert.icon';
import {
  PermissionCreateModel,
  PermissionFilter,
  PermissionModel,
  PermissionUpdateModel,
} from '../model/permission.model';

export type PermissionModalName = 'CREATE' | 'UPDATE';

export interface PermissionActiveModal {
  name: PermissionModalName;
}

export interface PermissionControllerState {
  list: PermissionModel[];
  listCount: number;
  message: string | null;
  loading: boolean;
  filter: PermissionFilter | null;
  page: number;
  pageRowAmount: number;
  activeModal: PermissionActiveModal | null;
  formUpdate: PermissionUpdateModel | null;
}

export interface PermissionControllerAction {
  getData: (startLoading?: boolean, filter?: PermissionFilter | null) => void;
  onPageChange: (page: number, rows: number) => void;
  onFilterChange: (filter?: PermissionFilter | null) => void;
  onHandleModal: (activeModal?: PermissionActiveModal | null) => void;
  onUpdateFormUpdate: (form: PermissionUpdateModel | null) => void;
  onSubmitCreate: (form: PermissionCreateModel) => void;
  onSubmitUpdate: (form: PermissionUpdateModel) => void;
  onToggleActive: (permission: PermissionModel) => void;
  onDelete: (uuid: string) => void;
}

const DEFAULT_FILTER: PermissionFilter = { skip: 0, take: 10 };

/** Toggling and deleting happen from the table, where `message` is not rendered. */
const showErrorAlert = (title: string, message: string) => {
  CusttomAlertController.getState().handleCustomAlert({
    title,
    message,
    type: CustomAlertType.ERROR,
    icon: CustomAlertIcons.deleteFile,
    negText: 'ປິດ',
    posText: 'ຕົກລົງ',
  });
};

const PermissionController = create<PermissionControllerState & PermissionControllerAction>()(
  (set, get) => ({
    loading: false,
    list: [],
    message: null,
    listCount: 0,
    page: 0,
    pageRowAmount: 10,
    filter: DEFAULT_FILTER,
    activeModal: null,
    formUpdate: null,

    getData: async (startLoading?: boolean, filter?: PermissionFilter | null) => {
      try {
        if (startLoading) set(() => ({ loading: true }));
        const res = await axiosInstance.get(ApiPath.permissions.all, {
          params: filter ?? DEFAULT_FILTER,
        });
        const list: PermissionModel[] = res?.data?.data ?? [];
        const listCount: number = res?.data?.meta?.total ?? 0;
        set(() => ({ list, listCount }));
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        if (startLoading) setTimeout(() => set(() => ({ loading: false })), 500);
      }
    },

    onPageChange: (page: number, rows: number) => {
      set(() => ({ pageRowAmount: rows, page }));
    },

    onFilterChange: (filter?: PermissionFilter | null) => {
      set(() => ({ filter: filter ?? null }));
      get().getData(true, filter);
    },

    onHandleModal: (activeModal?: PermissionActiveModal | null) => {
      set(() => ({ activeModal: activeModal ?? null, message: null }));
      if (!activeModal) set(() => ({ formUpdate: null }));
    },

    onUpdateFormUpdate: (form: PermissionUpdateModel | null) => {
      set(() => ({ formUpdate: form, message: null }));
    },

    onSubmitCreate: async (form: PermissionCreateModel) => {
      try {
        set(() => ({ loading: true, message: null }));
        const res = await axiosInstance.post(ApiPath.permissions.create, {
          code: form.code?.trim(),
          name: form.name?.trim(),
          module: form.module?.trim(),
          ...(form.description?.trim() ? { description: form.description.trim() } : {}),
        });
        if (res?.status >= 200 && res?.status < 300) {
          set(() => ({ page: 0 }));
          get().getData(true, get().filter);
          setTimeout(() => get().onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    },

    onSubmitUpdate: async (form: PermissionUpdateModel) => {
      if (!form?.uuid) {
        set(() => ({ message: 'Permission uuid is missing!' }));
        return;
      }
      try {
        set(() => ({ loading: true, message: null }));
        const { uuid, ...body } = form;
        const res = await axiosInstance.patch(ApiPath.permissions.update(uuid), {
          code: body.code?.trim(),
          name: body.name?.trim(),
          module: body.module?.trim(),
          description: body.description?.trim() ?? '',
          is_active: body.is_active ?? true,
        });
        if (res?.status >= 200 && res?.status < 300) {
          set(() => ({ formUpdate: null }));
          get().getData(true, get().filter);
          setTimeout(() => get().onHandleModal(null), 500);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        set(() => ({ message: axiosErrorMessage({ error: error as AxiosError }) }));
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    },

    onToggleActive: async (permission: PermissionModel) => {
      try {
        set(() => ({ loading: true, message: null }));
        const res = await axiosInstance.patch(ApiPath.permissions.update(permission.uuid), {
          code: permission.code,
          name: permission.name,
          module: permission.module,
          description: permission.description ?? '',
          is_active: !permission.is_active,
        });
        if (res?.status >= 200 && res?.status < 300) {
          get().getData(true, get().filter);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        const message = axiosErrorMessage({ error: error as AxiosError });
        set(() => ({ message }));
        showErrorAlert('ປ່ຽນສະຖານະບໍ່ສຳເລັດ', message);
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    },

    onDelete: async (uuid: string) => {
      try {
        set(() => ({ loading: true, message: null }));
        const res = await axiosInstance.delete(ApiPath.permissions.delete(uuid));
        if (res?.status >= 200 && res?.status < 300) {
          get().getData(true, get().filter);
        } else {
          set(() => ({ message: res?.statusText }));
        }
      } catch (error) {
        const message = axiosErrorMessage({ error: error as AxiosError });
        set(() => ({ message }));
        showErrorAlert('ລົບສິດທິບໍ່ສຳເລັດ', message);
      } finally {
        setTimeout(() => set(() => ({ loading: false })), 500);
      }
    },
  })
);

export default PermissionController;
